import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReactECharts from 'echarts-for-react';

const { FiShield, FiTrendingUp, FiX, FiDollarSign, FiCalendar, FiPercent, FiBarChart2 } = FiIcons;

function InsuranceVsMFCalculator({ onClose }) {
  const [formData, setFormData] = useState({
    annualPremium: 100000,
    maturityValue: 1890000,
    yearlyPayout: 15000,
    policyTerm: 20,
    premiumPayingTerm: 10,
    payoutFrequency: 'yearly', // yearly, monthly, quarterly
    mutualFundReturn: 12,
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Calculate yearly payout from frequency
  const getYearlyPayout = () => {
    const { yearlyPayout, payoutFrequency } = formData;
    
    switch (payoutFrequency) {
      case 'monthly':
        return yearlyPayout * 12;
      case 'quarterly':
        return yearlyPayout * 4;
      default:
        return yearlyPayout;
    }
  };

  // Comprehensive calculations
  const calculations = useMemo(() => {
    const {
      annualPremium,
      maturityValue,
      policyTerm,
      premiumPayingTerm,
      mutualFundReturn
    } = formData;

    const actualYearlyPayout = getYearlyPayout();

    // Total premium paid
    const totalPremiumPaid = annualPremium * premiumPayingTerm;

    // Generate year-wise cashflows for insurance
    const insuranceCashflows = [];
    let cumulativeInsuranceInflow = 0;
    let cumulativeInsuranceOutflow = 0;

    for (let year = 1; year <= policyTerm; year++) {
      let outflow = 0;
      let inflow = 0;

      // Premium payments during premium paying term
      if (year <= premiumPayingTerm) {
        outflow = annualPremium;
        cumulativeInsuranceOutflow += outflow;
      }

      // Yearly payouts after premium paying term
      if (year > premiumPayingTerm) {
        inflow = actualYearlyPayout;
        cumulativeInsuranceInflow += inflow;
      }

      // Maturity value in final year
      if (year === policyTerm) {
        inflow += maturityValue;
        cumulativeInsuranceInflow += maturityValue;
      }

      const netCashflow = inflow - outflow;

      insuranceCashflows.push({
        year,
        outflow,
        inflow,
        netCashflow,
        cumulativeOutflow: cumulativeInsuranceOutflow,
        cumulativeInflow: cumulativeInsuranceInflow,
        cumulativeNet: cumulativeInsuranceInflow - cumulativeInsuranceOutflow
      });
    }

    // Calculate IRR for insurance
    const calculateIRR = () => {
      const cashflows = insuranceCashflows.map(cf => cf.netCashflow);
      
      // Newton-Raphson method to find IRR
      let rate = 0.1; // Initial guess 10%
      const maxIterations = 1000;
      const tolerance = 0.000001;

      for (let i = 0; i < maxIterations; i++) {
        let npv = 0;
        let dnpv = 0;

        for (let t = 0; t < cashflows.length; t++) {
          npv += cashflows[t] / Math.pow(1 + rate, t + 1);
          dnpv -= (t + 1) * cashflows[t] / Math.pow(1 + rate, t + 2);
        }

        const newRate = rate - npv / dnpv;

        if (Math.abs(newRate - rate) < tolerance) {
          return newRate * 100; // Convert to percentage
        }

        rate = newRate;

        // Check for unrealistic values
        if (rate > 100 || rate < -1) {
          return null; // IRR cannot be calculated
        }
      }

      return null; // Did not converge
    };

    const irr = calculateIRR();

    // Mutual Fund calculations with same investment
    const mfCashflows = [];
    let mfCorpus = 0;
    let totalMFInvestment = 0;

    for (let year = 1; year <= policyTerm; year++) {
      let investment = 0;

      // Invest same premium amount during premium paying term
      if (year <= premiumPayingTerm) {
        investment = annualPremium;
        totalMFInvestment += investment;
      }

      // Calculate corpus growth
      mfCorpus = (mfCorpus + investment) * (1 + mutualFundReturn / 100);

      mfCashflows.push({
        year,
        investment,
        corpus: mfCorpus,
        returns: mfCorpus - totalMFInvestment
      });
    }

    const finalMFCorpus = mfCorpus;

    // Calculate total insurance inflow
    const totalInsuranceInflow = cumulativeInsuranceInflow;

    // Comparison metrics
    const difference = finalMFCorpus - totalInsuranceInflow;
    const percentageDifference = totalInsuranceInflow > 0 
      ? (difference / totalInsuranceInflow) * 100 
      : 0;

    // Calculate CAGR for insurance
    const insuranceCAGR = totalInsuranceInflow > 0 && totalPremiumPaid > 0
      ? (Math.pow(totalInsuranceInflow / totalPremiumPaid, 1 / policyTerm) - 1) * 100
      : 0;

    // Calculate CAGR for MF
    const mfCAGR = mutualFundReturn;

    return {
      // Insurance metrics
      totalPremiumPaid,
      maturityValue,
      yearlyPayout: actualYearlyPayout,
      totalInsuranceInflow,
      insuranceNetGain: totalInsuranceInflow - totalPremiumPaid,
      insuranceCashflows,
      irr,
      insuranceCAGR,

      // Mutual Fund metrics
      totalMFInvestment,
      finalMFCorpus,
      mfReturns: finalMFCorpus - totalMFInvestment,
      mfCashflows,
      mfCAGR,

      // Comparison
      difference,
      percentageDifference,
      betterOption: difference > 0 ? 'Mutual Fund' : 'Insurance',
      betterBy: Math.abs(difference)
    };
  }, [formData, getYearlyPayout]);

  // Chart: Cashflow Comparison
  const cashflowComparisonChart = {
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const year = params[0].axisValue;
        return `
          <strong>Year ${year}</strong><br/>
          Insurance Net: ${formatCurrency(params[0].value)}<br/>
          MF Corpus: ${formatCurrency(params[1].value)}
        `;
      }
    },
    legend: {
      data: ['Insurance Net Cashflow', 'Mutual Fund Corpus'],
      top: 25
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: calculations.insuranceCashflows.map(item => item.year),
      name: 'Year'
    },
    yAxis: {
      type: 'value',
      name: 'Amount (₹)',
      axisLabel: {
        formatter: (value) => `₹${(value / 100000).toFixed(0)}L`
      }
    },
    series: [
      {
        name: 'Insurance Net Cashflow',
        type: 'line',
        data: calculations.insuranceCashflows.map(item => item.cumulativeNet),
        itemStyle: { color: '#3b82f6' },
        areaStyle: { color: 'rgba(59, 130, 246, 0.1)' },
        lineStyle: { width: 3 }
      },
      {
        name: 'Mutual Fund Corpus',
        type: 'line',
        data: calculations.mfCashflows.map(item => item.corpus),
        itemStyle: { color: '#10b981' },
        areaStyle: { color: 'rgba(16, 185, 129, 0.1)' },
        lineStyle: { width: 3 }
      }
    ]
  };

  // Chart: Corpus Growth Comparison
  const corpusGrowthChart = {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left'
    },
    series: [
      {
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        label: {
          show: true,
          formatter: '{b}\n{c}'
        },
        data: [
          {
            value: calculations.totalInsuranceInflow,
            name: 'Insurance Total',
            itemStyle: { color: '#3b82f6' }
          },
          {
            value: calculations.finalMFCorpus,
            name: 'Mutual Fund Corpus',
            itemStyle: { color: '#10b981' }
          }
        ]
      }
    ]
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[95vh] overflow-y-auto my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-[#19b48d] flex items-center justify-center">
              <SafeIcon icon={FiShield} className="text-white text-sm" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Insurance vs Mutual Fund Calculator
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Input Section */}
            <div className="lg:col-span-1 space-y-6">
              {/* Insurance Policy Details */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Insurance Policy Details</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Annual Premium ₹
                    </label>
                    <input
                      type="number"
                      value={formData.annualPremium}
                      onChange={(e) => handleInputChange('annualPremium', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(formData.annualPremium)}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Maturity Value ₹
                    </label>
                    <input
                      type="number"
                      value={formData.maturityValue}
                      onChange={(e) => handleInputChange('maturityValue', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(formData.maturityValue)}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payout Frequency
                    </label>
                    <select
                      value={formData.payoutFrequency}
                      onChange={(e) => handleInputChange('payoutFrequency', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    >
                      <option value="yearly">Yearly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {formData.payoutFrequency === 'yearly' ? 'Yearly' : formData.payoutFrequency === 'quarterly' ? 'Quarterly' : 'Monthly'} Payout ₹
                    </label>
                    <input
                      type="number"
                      value={formData.yearlyPayout}
                      onChange={(e) => handleInputChange('yearlyPayout', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.payoutFrequency === 'yearly' 
                        ? formatCurrency(formData.yearlyPayout) + '/year'
                        : formData.payoutFrequency === 'quarterly'
                        ? formatCurrency(formData.yearlyPayout) + '/quarter (₹' + (formData.yearlyPayout * 4).toLocaleString() + '/year)'
                        : formatCurrency(formData.yearlyPayout) + '/month (₹' + (formData.yearlyPayout * 12).toLocaleString() + '/year)'
                      }
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Policy Term (Years)
                    </label>
                    <input
                      type="number"
                      value={formData.policyTerm}
                      onChange={(e) => handleInputChange('policyTerm', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                      min="1"
                      max="50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Premium Paying Term (Years)
                    </label>
                    <input
                      type="number"
                      value={formData.premiumPayingTerm}
                      onChange={(e) => handleInputChange('premiumPayingTerm', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                      min="1"
                      max={formData.policyTerm}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Payouts start after year {formData.premiumPayingTerm}
                    </p>
                  </div>
                </div>
              </div>

              {/* Mutual Fund Assumptions */}
              <div className="bg-green-50 rounded-xl shadow-sm border border-green-200 p-6">
                <h4 className="text-md font-semibold text-green-900 mb-4">Mutual Fund Assumptions</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-2">
                      Expected Annual Return (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.mutualFundReturn}
                      onChange={(e) => handleInputChange('mutualFundReturn', e.target.value)}
                      className="w-full px-4 py-3 border border-green-300 bg-white rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent"
                      min="0"
                      max="30"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div className="lg:col-span-2 space-y-6">
              {/* Key Metrics Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Insurance Results */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-200 p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <SafeIcon icon={FiShield} className="text-blue-600 text-2xl" />
                    <h4 className="text-lg font-semibold text-blue-900">Insurance Policy</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700">Total Premium Paid:</span>
                      <span className="text-lg font-bold text-blue-900">{formatCurrency(calculations.totalPremiumPaid)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700">Maturity Value:</span>
                      <span className="text-lg font-bold text-blue-900">{formatCurrency(calculations.maturityValue)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700">Yearly Payout:</span>
                      <span className="text-lg font-bold text-blue-900">{formatCurrency(calculations.yearlyPayout)}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-blue-300 pt-3">
                      <span className="text-sm text-blue-700">Total Inflow:</span>
                      <span className="text-lg font-bold text-blue-900">{formatCurrency(calculations.totalInsuranceInflow)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700">Net Gain:</span>
                      <span className={`text-lg font-bold ${calculations.insuranceNetGain >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(calculations.insuranceNetGain)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700">IRR:</span>
                      <span className="text-lg font-bold text-blue-900">
                        {calculations.irr !== null ? `${calculations.irr.toFixed(2)}%` : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700">CAGR:</span>
                      <span className="text-lg font-bold text-blue-900">
                        {calculations.insuranceCAGR.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Mutual Fund Results */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-sm border border-green-200 p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <SafeIcon icon={FiTrendingUp} className="text-green-600 text-2xl" />
                    <h4 className="text-lg font-semibold text-green-900">Mutual Fund</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-green-700">Total Investment:</span>
                      <span className="text-lg font-bold text-green-900">{formatCurrency(calculations.totalMFInvestment)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-green-700">Final Corpus:</span>
                      <span className="text-lg font-bold text-green-900">{formatCurrency(calculations.finalMFCorpus)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-green-700">Returns Generated:</span>
                      <span className="text-lg font-bold text-green-900">{formatCurrency(calculations.mfReturns)}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-green-300 pt-3">
                      <span className="text-sm text-green-700">CAGR:</span>
                      <span className="text-lg font-bold text-green-900">{calculations.mfCAGR.toFixed(2)}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Winner Comparison */}
              <div className={`rounded-xl shadow-sm border p-6 ${
                calculations.betterOption === 'Mutual Fund' 
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' 
                  : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
              }`}>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Comparison Result</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-gray-700 mb-1">Better Option</p>
                    <p className={`text-3xl font-bold ${
                      calculations.betterOption === 'Mutual Fund' ? 'text-green-700' : 'text-blue-700'
                    }`}>
                      {calculations.betterOption}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-700 mb-1">Better By</p>
                    <p className="text-3xl font-bold text-gray-900">{formatCurrency(calculations.betterBy)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-700 mb-1">Percentage Difference</p>
                    <p className="text-3xl font-bold text-gray-900">{Math.abs(calculations.percentageDifference).toFixed(2)}%</p>
                  </div>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Cumulative Wealth Comparison</h4>
                  <div className="h-80">
                    <ReactECharts option={cashflowComparisonChart} style={{ height: '100%', width: '100%' }} />
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Final Corpus Distribution</h4>
                  <div className="h-80">
                    <ReactECharts option={corpusGrowthChart} style={{ height: '100%', width: '100%' }} />
                  </div>
                </div>
              </div>

              {/* Year-wise Cashflow Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Year-wise Cashflow Breakdown</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Year</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Insurance Premium</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Insurance Inflow</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Insurance Net</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">MF Investment</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">MF Corpus</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">MF Returns</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calculations.insuranceCashflows.map((row, index) => {
                        const mfRow = calculations.mfCashflows[index];
                        return (
                          <tr key={index} className="border-b border-gray-100">
                            <td className="py-3 px-4 font-medium text-gray-900">{row.year}</td>
                            <td className="py-3 px-4 text-right text-red-600">
                              {row.outflow > 0 ? formatCurrency(row.outflow) : '-'}
                            </td>
                            <td className="py-3 px-4 text-right text-green-600">
                              {row.inflow > 0 ? formatCurrency(row.inflow) : '-'}
                            </td>
                            <td className="py-3 px-4 text-right font-medium text-gray-900">
                              {formatCurrency(row.cumulativeNet)}
                            </td>
                            <td className="py-3 px-4 text-right text-red-600">
                              {mfRow.investment > 0 ? formatCurrency(mfRow.investment) : '-'}
                            </td>
                            <td className="py-3 px-4 text-right font-medium text-green-700">
                              {formatCurrency(mfRow.corpus)}
                            </td>
                            <td className="py-3 px-4 text-right text-green-600">
                              {formatCurrency(mfRow.returns)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Key Insights */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Key Insights</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Investment Period:</strong> You will pay {formatCurrency(formData.annualPremium)}/year for {formData.premiumPayingTerm} years, 
                      totaling {formatCurrency(calculations.totalPremiumPaid)}.
                    </p>
                  </div>

                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Insurance Returns:</strong> After {formData.premiumPayingTerm} years, you will receive {formatCurrency(calculations.yearlyPayout)}/year 
                      for {formData.policyTerm - formData.premiumPayingTerm} years, plus {formatCurrency(formData.maturityValue)} at maturity.
                    </p>
                  </div>

                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Insurance IRR:</strong> The Internal Rate of Return (IRR) for this insurance policy is {
                        calculations.irr !== null ? `${calculations.irr.toFixed(2)}%` : 'not calculable (likely negative or very low)'
                      }.
                    </p>
                  </div>

                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Mutual Fund Alternative:</strong> If you invested the same {formatCurrency(formData.annualPremium)}/year in mutual funds 
                      at {formData.mutualFundReturn}% return, you would accumulate {formatCurrency(calculations.finalMFCorpus)} in {formData.policyTerm} years.
                    </p>
                  </div>

                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Winner:</strong> {calculations.betterOption} is better by {formatCurrency(calculations.betterBy)} ({Math.abs(calculations.percentageDifference).toFixed(2)}%).
                    </p>
                  </div>

                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Recommendation:</strong> {
                        calculations.betterOption === 'Mutual Fund' 
                          ? 'Consider investing in mutual funds for potentially higher returns. However, insurance provides guaranteed payouts and life cover.'
                          : 'The insurance policy offers better returns in this scenario, along with life cover benefits.'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default InsuranceVsMFCalculator;